import { randomUUID } from 'crypto';

export interface DomainProperties {
  name: string;
  description?: string;
  vision?: string;
  ubiquitousLanguage: Record<string, string>;
}

export class Domain {
  readonly id: string;
  readonly createdAt: Date;
  private _updatedAt: Date;
  private _name: string;
  private _description?: string;
  private _vision?: string;
  private _ubiquitousLanguage: Record<string, string>;

  private constructor(properties: DomainProperties, id?: string) {
    this.id = id || randomUUID();
    this.createdAt = new Date();
    this._updatedAt = new Date();
    this._name = properties.name;
    this._description = properties.description;
    this._vision = properties.vision;
    this._ubiquitousLanguage = properties.ubiquitousLanguage;

    this.validateInvariants();
  }

  static create(properties: DomainProperties): Domain {
    return new Domain(properties);
  }

  static reconstitute(properties: DomainProperties, id: string, createdAt: Date, updatedAt: Date): Domain {
    const domain = new Domain(properties, id);
    (domain as any).createdAt = createdAt;
    domain._updatedAt = updatedAt;
    return domain;
  }

  get name(): string {
    return this._name;
  }

  get description(): string | undefined {
    return this._description;
  }

  get vision(): string | undefined {
    return this._vision;
  }

  get ubiquitousLanguage(): Record<string, string> {
    return { ...this._ubiquitousLanguage };
  }


  get updatedAt(): Date {
    return this._updatedAt;
  }

  updateName(name: string): void {
    if (!name || name.trim().length === 0) {
      throw new Error('Domain name cannot be empty');
    }
    this._name = name.trim();
    this.touch();
  }

  updateDescription(description?: string): void {
    this._description = description?.trim() || undefined;
    this.touch();
  }

  updateVision(vision?: string): void {
    this._vision = vision?.trim() || undefined;
    this.touch();
  }

  addUbiquitousLanguageTerm(term: string, definition: string): void {
    if (!term || !definition) {
      throw new Error('Term and definition are required');
    }
    this._ubiquitousLanguage[term.trim()] = definition.trim();
    this.touch();
  }

  removeUbiquitousLanguageTerm(term: string): void {
    delete this._ubiquitousLanguage[term];
    this.touch();
  }


  private touch(): void {
    this._updatedAt = new Date();
  }

  private validateInvariants(): void {
    if (!this._name || this._name.trim().length === 0) {
      throw new Error('Domain name is required');
    }

    if (this._name.length > 100) {
      throw new Error('Domain name must be 100 characters or less');
    }

    if (this._description && this._description.length > 1000) {
      throw new Error('Domain description must be 1000 characters or less');
    }

    if (this._vision && this._vision.length > 2000) {
      throw new Error('Domain vision must be 2000 characters or less');
    }
  }

  toJSON() {
    return {
      id: this.id,
      name: this._name,
      description: this._description,
      vision: this._vision,
      ubiquitousLanguage: this._ubiquitousLanguage,
      createdAt: this.createdAt,
      updatedAt: this._updatedAt
    };
  }
}